import { cn } from "@/common/helpers/utils";
import { useDevice } from "@/common/hooks/use-device";
import { DataListPagination } from "@/components/data-list/_components/data-list-pagination";
import { FilterContainer } from "@/components/filter/filter-container";
import { ResponsiveSearch } from "@/components/search-input/responsive-search";
import { CommandButton } from "@/components/command-palette/command-button";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PlusCircleIcon, SortAsc, SortDesc } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DataListHeaderProps, SortOption } from "../types";

export function DataListHeader({
  headerComponent,
  features,
  filters,
  activeFilterCount,
  searchTerm,
  searchPlaceholder,
  totalItems,
  paginationState,
  isLoading,
  onFilterChange,
  onClearAllFilters,
  onSearchChange,
  onClearSearch,
  currentFilterValues,
  sortingOptions,
  currentSorting = [],
  onSortingChange,
  createButtonLink,
  createButtonText,
  onPaginationChange,
}: DataListHeaderProps) {
  const { t } = useTranslation("common");
  const { isMobile } = useDevice();
  const showFilters = filters.length > 0;
  const showSearch = features.search;
  const showSorting = features.sorting && sortingOptions && sortingOptions.length > 0;
  const showCreateAction = !!createButtonLink;
  const showPagination = features.pagination && totalItems > 0 && paginationState && !!onPaginationChange;

  const handleSortChange = (field: string) => {
    if (!onSortingChange) return;

    const existingSort = currentSorting.find((sort) => sort.field === field);
    let newSorting: SortOption[];

    if (existingSort) {
      if (existingSort.direction === "asc") {
        newSorting = currentSorting.map((sort) =>
          sort.field === field ? { ...sort, direction: "desc" as const } : sort,
        );
      } else {
        newSorting = currentSorting.filter((sort) => sort.field !== field);
      }
    } else {
      newSorting = [...currentSorting, { field, direction: "asc" as const }];
    }

    onSortingChange(newSorting);
  };

  const getSortIcon = (field: string) => {
    const sort = currentSorting.find((s) => s.field === field);
    if (!sort) return null;
    return sort.direction === "asc" ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />;
  };

  const renderFilters = () => {
    if (!showFilters) return null;

    return (
      <FilterContainer activeFilterCount={activeFilterCount} onClearAll={onClearAllFilters}>
        {filters.map((filter) => (
          <div key={filter.id}>
            <label className="text-sm font-medium">{filter.label}</label>
            <div className="mt-2 space-y-1">
              {filter.options.map((option) => {
                const isSelected = currentFilterValues[filter.id]?.includes(option.value) || false;
                return (
                  <label
                    key={option.value}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
                      "hover:bg-muted/60",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const currentValues = currentFilterValues[filter.id] || [];
                        const newValues = e.target.checked
                          ? [...currentValues, option.value]
                          : currentValues.filter((v) => v !== option.value);
                        onFilterChange(filter.id, newValues);
                      }}
                      className="rounded"
                    />
                    {option.label}
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </FilterContainer>
    );
  };

  return (
    <>
      {headerComponent && isMobile && (
        <div className="bg-background/98 border-border relative z-50 flex shrink-0 items-center gap-2 backdrop-blur-sm lg:gap-3">
          {headerComponent}
        </div>
      )}
      <div
        className={cn(
          "bg-background/98 border-border relative flex shrink-0 flex-wrap items-center gap-2 py-2 backdrop-blur-sm lg:gap-3",
        )}
      >
        {showCreateAction && (
          <CommandButton
            commandId={`create-${createButtonLink}`}
            label={createButtonText ?? t("common:Add")}
            icon={<PlusCircleIcon className="h-4 w-4" />}
            to={createButtonLink!}
            className="h-9 shrink-0 gap-2"
          />
        )}

        {headerComponent && !isMobile && (
          <div className="flex shrink-0 items-center gap-2 lg:gap-3">{headerComponent}</div>
        )}

        <div className="flex min-w-0 flex-1 items-center justify-end gap-2 overflow-visible lg:gap-3">
          {renderFilters()}

          {showSorting && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={currentSorting.length > 0 ? ButtonVariant.Filled : ButtonVariant.Outlined}
                  colorVariant={currentSorting.length > 0 ? ButtonColorVariant.Primary : ButtonColorVariant.Secondary}
                  size={ButtonSize.Icon}
                  className="h-9 w-9 shrink-0"
                >
                  <SortAsc className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuLabel>{t("SortBy")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {sortingOptions!.map((option) => (
                  <DropdownMenuCheckboxItem
                    key={option.field}
                    checked={currentSorting.some((sort) => sort.field === option.field)}
                    onCheckedChange={() => handleSortChange(option.field)}
                  >
                    <div className="flex w-full items-center justify-between">
                      <span>{option.label}</span>
                      {getSortIcon(option.field)}
                    </div>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {showSearch && (
            <ResponsiveSearch
              placeholder={searchPlaceholder}
              onSearchChange={onSearchChange}
              onChange={onSearchChange}
              onClear={onClearSearch}
              value={searchTerm}
            />
          )}

          {showPagination && (
            <DataListPagination
              paginationState={paginationState!}
              totalItems={totalItems!}
              onPaginationChange={onPaginationChange!}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
    </>
  );
}
